import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { H5pEditor, type H5pEditorHandle } from '@/components/h5p/H5pEditor';
import { withLibraryContext } from '@/lib/library-navigation';

interface H5pConstructorPageProps {
  packageId?: string;
  mode?: 'create' | 'edit';
}

export function H5pConstructorPage({ packageId, mode = 'create' }: H5pConstructorPageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editorRef = useRef<H5pEditorHandle>(null);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const isEdit = mode === 'edit' || Boolean(packageId);
  const backUrl = isEdit && packageId ? withLibraryContext(`/h5p/${packageId}`, searchParams) : '/h5p';
  const backLabel = isEdit ? 'Volver al detalle de la actividad' : 'Volver a Biblioteca H5P';

  return (
    <div className="lms-h5p-constructor lms-site-admin-hub-page font-sans">
      <header className="lms-h5p-constructor__header mb-6">
        <div className="flex items-start gap-4">
          <HubBackLink to={backUrl} label={backLabel} />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
              {isEdit ? 'Editar actividad H5P' : 'Crear actividad H5P'}
            </h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              {isEdit
                ? 'Modifica el contenido y la configuración de la actividad H5P'
                : 'Selector oficial H5P Content Hub — elige un tipo, instálalo con «Obtener» y edita la actividad.'}
            </p>
          </div>
        </div>
        {!isEdit ? (
          <Link to="/h5p/create" className="lms-h5p-constructor__import">
            <span className="material-symbols-outlined" aria-hidden="true">
              upload_file
            </span>
            Importar .h5p
          </Link>
        ) : null}
      </header>

      <div className="lms-h5p-constructor__shell">
        <H5pEditor
          ref={editorRef}
          packageId={packageId}
          onStatusChange={setStatus}
          onSavingChange={setSaving}
          onSaved={(id) =>
            navigate(packageId ? withLibraryContext(`/h5p/${packageId}`, searchParams) : `/h5p/${id}`)
          }
        />
      </div>

      <footer className="lms-h5p-constructor__footer">
        {status ? <p className="lms-h5p-constructor__status">{status}</p> : null}
        <div className="lms-h5p-constructor__actions">
          <Link to="/h5p" className="lms-h5p-constructor__cancel">
            Cancelar
          </Link>
          <button
            type="button"
            className="lms-h5p-constructor__save"
            disabled={saving}
            onClick={() => editorRef.current?.save()}
          >
            {saving ? 'Guardando…' : 'Guardar actividad'}
          </button>
        </div>
      </footer>
    </div>
  );
}
